import React, { useEffect, useState } from 'react';
import { X, ArrowUpRight, CheckCircle2, FileText, Film, Download, Camera, Award, ImageOff } from 'lucide-react';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from './ui/dialog';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { useToast } from '../hooks/use-toast';
import { addLead, fileUrl } from '../api';
import { COMPANY } from '../mock';

const inputCls = 'mt-1.5 bg-white border-slate-300 text-slate-900 placeholder:text-slate-400 focus-visible:ring-[#f5a524]';

function ProductMedia({ media }) {
  if (!media) return null;
  const foto = media.foto || [];
  const filme = media.filme || [];
  const docs = [...(media.certificari || []), ...(media.tehnice || [])];
  if (!foto.length && !filme.length && !docs.length) return null;
  return (
    <div className="mt-6 space-y-4" data-testid="product-media">
      {foto.length > 0 && (
        <div>
          <p className="text-[11px] uppercase tracking-[0.15em] text-slate-400 mb-2">Galerie foto</p>
          <div className="grid grid-cols-4 gap-2">
            {foto.map((it, i) => (
              <a key={i} href={fileUrl(it.url)} target="_blank" rel="noreferrer" className="aspect-square overflow-hidden border border-slate-200 bg-slate-100">
                <img src={fileUrl(it.url)} alt={it.name} className="w-full h-full object-cover hover:scale-105 transition-transform" />
              </a>
            ))}
          </div>
        </div>
      )}
      {filme.length > 0 && (
        <div>
          <p className="text-[11px] uppercase tracking-[0.15em] text-slate-400 mb-2">Filme</p>
          <div className="space-y-2">
            {filme.map((it, i) => (
              <video key={i} src={fileUrl(it.url)} controls className="w-full border border-slate-200 bg-black" />
            ))}
          </div>
        </div>
      )}
      {docs.length > 0 && (
        <div>
          <p className="text-[11px] uppercase tracking-[0.15em] text-slate-400 mb-2">Certificări & documente tehnice</p>
          <div className="space-y-1.5">
            {docs.map((it, i) => (
              <a key={i} href={fileUrl(it.url)} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-[13px] text-slate-700 hover:text-[#d97706] bg-slate-50 border border-slate-200 px-3 py-2 transition-colors">
                {it.type === 'video' ? <Film size={15} /> : <FileText size={15} />}
                <span className="flex-1 truncate">{it.name}</span>
                <Download size={14} className="text-slate-400" />
              </a>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export function ProductDialog({ product, onClose, onQuote }) {
  const open = !!product;
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white border border-slate-200 text-slate-900 max-w-[720px] p-0 overflow-hidden gap-0 max-h-[90vh] overflow-y-auto">
        {product && (
          <div className="grid grid-cols-1 md:grid-cols-2">
            <DialogTitle className="sr-only">{product.name}</DialogTitle>
            <DialogDescription className="sr-only">{product.description}</DialogDescription>
            <div className="relative aspect-square md:aspect-auto bg-slate-100">
              <img src={fileUrl(product.image)} alt={product.name} className="w-full h-full object-cover" />
            </div>
            <div className="p-7">
              <span className="text-[10px] tracking-[0.15em] uppercase font-bold text-[#d97706]">{product.category}</span>
              <h3 className="font-display font-bold text-slate-900 text-2xl leading-tight mt-2 mb-1">{product.name}</h3>
              <p className="text-[13px] text-slate-400 mb-4">{product.tag}</p>
              <p className="text-[14px] text-slate-600 leading-relaxed mb-5">{product.description}</p>
              <div className="space-y-2 mb-6 text-[13.5px]">
                <div className="flex justify-between border-b border-slate-200 pb-2">
                  <span className="text-slate-400">Utilizare</span>
                  <span className="text-slate-900 text-right">{product.useCase}</span>
                </div>
                <div className="flex justify-between border-b border-slate-200 pb-2">
                  <span className="text-slate-400">Preț</span>
                  <span className="font-display font-semibold text-[#d97706]">{product.price}</span>
                </div>
              </div>
              <button
                onClick={() => { onClose(); onQuote(product); }}
                data-testid="product-dialog-quote-btn"
                className="btn-accent w-full py-3 text-[14px] uppercase tracking-wide flex items-center justify-center gap-2"
              >
                Cere ofertă pentru acest produs <ArrowUpRight size={17} />
              </button>
            </div>
            <div className="md:col-span-2 px-7 pb-7">
              <ProductMedia media={product.media} />
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

const EMPTY = { name: '', company: '', phone: '', email: '', message: '' };

const GALLERY_META = {
  foto: { label: 'Foto', icon: Camera },
  filme: { label: 'Filme', icon: Film },
  tehnice: { label: 'Documente tehnice', icon: FileText },
  certificari: { label: 'Certificări', icon: Award },
};

export function MediaGalleryDialog({ category, items, onQuote, onClose }) {
  const open = !!category;
  const [lightbox, setLightbox] = useState(null);
  useEffect(() => { if (!open) setLightbox(null); }, [open]);
  const meta = category ? GALLERY_META[category] : null;
  const Icon = meta?.icon || Camera;
  const list = items || [];

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white border border-slate-200 text-slate-900 max-w-[860px] p-0 overflow-hidden gap-0 max-h-[90vh] overflow-y-auto" data-testid="media-gallery-dialog">
        <div className="p-6 sm:p-7">
          <div className="flex items-start justify-between mb-5">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 bg-amber-50 flex items-center justify-center shrink-0">
                <Icon size={20} className="text-[#f5a524]" />
              </div>
              <div>
                <DialogTitle asChild><h3 className="font-display font-bold text-xl leading-tight">{meta?.label}</h3></DialogTitle>
                <DialogDescription className="text-[13px] text-slate-400 mt-0.5">{list.length} {list.length === 1 ? 'element' : 'elemente'} din portofoliul EGEA</DialogDescription>
              </div>
            </div>
            <button type="button" onClick={onClose} data-testid="gallery-close-button" className="text-slate-400 hover:text-slate-900 transition-colors">
              <X size={20} />
            </button>
          </div>

          {list.length === 0 ? (
            <div className="py-16 text-center">
              <ImageOff size={34} className="text-slate-300 mx-auto mb-4" />
              <p className="text-[14px] text-slate-500 max-w-[380px] mx-auto">Momentan nu avem {meta?.label.toLowerCase()} publicate aici. Cere o ofertă și îți trimitem materialele complete.</p>
              <button onClick={() => { onClose(); onQuote(); }} className="btn-accent px-6 py-3 text-[13px] uppercase tracking-wide mt-6 inline-flex items-center gap-2">
                Cere ofertă <ArrowUpRight size={16} />
              </button>
            </div>
          ) : category === 'foto' ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2.5">
              {list.map((it, i) => (
                <button key={i} type="button" onClick={() => setLightbox(it)} className="relative aspect-square overflow-hidden border border-slate-200 bg-slate-100 group">
                  <img src={fileUrl(it.url)} alt={it.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
                  {it.product && <span className="absolute bottom-0 left-0 right-0 text-[10px] text-white/90 bg-gradient-to-t from-black/80 to-transparent px-2 py-1.5 truncate text-left">{it.product}</span>}
                </button>
              ))}
            </div>
          ) : category === 'filme' ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {list.map((it, i) => (
                <div key={i}>
                  <video src={fileUrl(it.url)} controls className="w-full border border-slate-200 bg-black" />
                  {it.product && <p className="text-[12px] text-slate-400 mt-1.5">{it.product}</p>}
                </div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {list.map((it, i) => (
                <a key={i} href={fileUrl(it.url)} target="_blank" rel="noreferrer" className="flex items-center gap-2.5 text-[13px] text-slate-700 hover:text-[#d97706] bg-slate-50 border border-slate-200 px-3.5 py-3 transition-colors">
                  <FileText size={16} className="shrink-0 text-[#f5a524]" />
                  <span className="flex-1 min-w-0">
                    <span className="block truncate">{it.name}</span>
                    {it.product && <span className="block text-[11px] text-slate-400 truncate">{it.product}</span>}
                  </span>
                  <Download size={15} className="text-slate-400 shrink-0" />
                </a>
              ))}
            </div>
          )}
        </div>

        {lightbox && (
          <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4" onClick={() => setLightbox(null)}>
            <button className="absolute top-5 right-5 text-white/80 hover:text-white" onClick={() => setLightbox(null)}><X size={26} /></button>
            <img src={fileUrl(lightbox.url)} alt={lightbox.name} className="max-w-full max-h-[88vh] object-contain" onClick={(e) => e.stopPropagation()} />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export function QuoteDialog({ open, onClose, prefillProduct, prefillContact }) {
  const { toast } = useToast();
  const [form, setForm] = useState(EMPTY);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    if (open) {
      setSent(false);
      setForm(prefillContact ? {
        ...EMPTY,
        name: prefillContact.name || '',
        company: prefillContact.company || '',
        phone: prefillContact.phone || '',
        email: prefillContact.email || '',
      } : EMPTY);
    }
  }, [open, prefillContact]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone) {
      toast({ title: 'Completează câmpurile obligatorii', description: 'Nume și telefon sunt necesare.' });
      return;
    }
    try {
      await addLead({ ...form, product: prefillProduct ? prefillProduct.name : 'General' });
      setSent(true);
      toast({ title: 'Cerere trimisă!', description: 'Te contactăm în maxim 24h cu o ofertă.' });
    } catch (err) {
      toast({ title: 'Eroare', description: 'Cererea nu a putut fi trimisă. Încearcă din nou sau sună-ne.' });
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white border border-slate-200 text-slate-900 max-w-[520px] p-0 overflow-hidden gap-0">
        {sent ? (
          <div className="p-10 text-center">
            <div className="h-14 w-14 rounded-full bg-amber-50 flex items-center justify-center mx-auto mb-5">
              <CheckCircle2 size={30} className="text-[#f5a524]" />
            </div>
            <h3 className="font-display font-bold uppercase text-2xl mb-2">Mulțumim!</h3>
            <p className="text-[14.5px] text-slate-500 leading-relaxed max-w-[360px] mx-auto">
              Cererea ta a fost înregistrată. Echipa EGEA te contactează în maxim 24h cu specificații și preț.
            </p>
            <button onClick={onClose} className="btn-accent px-6 py-3 text-[14px] uppercase tracking-wide mt-7">Închide</button>
          </div>
        ) : (
          <form onSubmit={submit} className="p-7">
            <div className="flex items-start justify-between mb-1">
              <div>
                <span className="section-eyebrow">Cere ofertă</span>
                <DialogTitle asChild><h3 className="font-display font-bold uppercase text-2xl mt-2">Ofertă tehnică în 24h</h3></DialogTitle>
                <DialogDescription className="sr-only">Formular cerere de ofertă</DialogDescription>
              </div>
              <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-900 transition-colors">
                <X size={20} />
              </button>
            </div>
            {prefillProduct && (
              <div className="mt-3 mb-2 text-[13px] text-slate-700 bg-slate-50 border border-slate-200 px-3 py-2">
                Produs: <span className="text-[#d97706] font-semibold">{prefillProduct.name}</span>
              </div>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4">
              <div>
                <Label className="text-[12px] text-slate-500">Nume *</Label>
                <Input value={form.name} onChange={set('name')} placeholder="Numele tău" data-testid="quote-name-input" className={inputCls} />
              </div>
              <div>
                <Label className="text-[12px] text-slate-500">Telefon *</Label>
                <Input value={form.phone} onChange={set('phone')} placeholder="07xx xxx xxx" data-testid="quote-phone-input" className={inputCls} />
              </div>
              <div>
                <Label className="text-[12px] text-slate-500">Companie</Label>
                <Input value={form.company} onChange={set('company')} placeholder="Firma (opțional)" className={inputCls} />
              </div>
              <div>
                <Label className="text-[12px] text-slate-500">Email</Label>
                <Input value={form.email} onChange={set('email')} placeholder="email@exemplu.ro" className={inputCls} />
              </div>
            </div>
            <div className="mt-3">
              <Label className="text-[12px] text-slate-500">Detalii proiect</Label>
              <Textarea value={form.message} onChange={set('message')} rows={3} placeholder="Dimensiuni, cantitate, locație, termen..." className={`${inputCls} resize-none`} />
            </div>
            <button type="submit" data-testid="quote-submit-button" className="btn-accent w-full py-3.5 text-[14px] uppercase tracking-wide flex items-center justify-center gap-2 mt-5">
              Trimite cererea <ArrowUpRight size={17} />
            </button>
            <p className="text-[12px] text-slate-400 text-center mt-3">
              Sau sună direct la <a href={COMPANY.phoneHref} className="text-[#d97706] font-medium">{COMPANY.phone}</a>
            </p>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
